import { actions, MarketDataStore, MARKET_DATA_STORE_NAME, IStore } from './store';
import { frontendActions, getConfig, LocationType } from '@cmctechnology/webinvest-store-frontend';
import { CmcWebApi, IWebApiRequestBody, webTradingApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { Dispatch } from '@reduxjs/toolkit';
import { QuoteKey } from './types';
import { MARKET_DATA_API_NAMES } from './models/marketDataApiNames';
import isEqual from 'lodash.isequal';
import { getQuotesUseWs, subscribeWsQuotes } from './wsActions';

type ResourceKey = number | string;
type GetState = typeof MarketDataStore.getState;

export const fetchQuoteBatch = (instruments: QuoteKey[]) => {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const quotes = getState()[MARKET_DATA_STORE_NAME].quotes;

    const request: CmcWebApi.MarketInformation.QuoteBatchRequest2 & IWebApiRequestBody = {
      AccountNumbers: [],
      IncludeCompanyInfo: false,
      IncludeFxRates: false,
      IncludeSecurityExcludeRuleResults: false,
      OverrideDelayed: false,
      Requests: instruments.map((instrument) => ({
        ExchangeId: instrument.exchangeId,
        StockCode: instrument.instrumentCode,
        Hash: quotes?.[instrument.instrumentCode]?.exchanges[instrument.exchangeId]?.hash ?? '0'
      }))
    };

    const callWebApi = () =>
      webTradingApi(getConfig().stockbrokingApiUrl).send<CmcWebApi.MarketInformation.QuoteBatchModel3>({ [MARKET_DATA_API_NAMES.QuoteBatchGet3]: request });

    try {
      const response = await frontendActions.apiCall(MARKET_DATA_STORE_NAME, MARKET_DATA_API_NAMES.QuoteBatchGet3, callWebApi)(dispatch);
      dispatch<any>(actions.quoteFetched(response.Quotes));
    } catch {}
  };
};

export const fetchQuotesByWs = (quotes: QuoteKey[]) => {
  return (dispatch: Dispatch<any>, getState: () => IStore) => {
    const subs = getState()[MARKET_DATA_STORE_NAME].quoteSubscriptions;
    /* istanbul ignore else */
    if (!isEqual(quotes, subs)) {
      dispatch(actions.quotesSubscribed(quotes));
      dispatch(subscribeWsQuotes());
    }
  };
};

// eslint-disable-next-line unicorn/consistent-function-scoping
export const onPollQuote = (subscriptions: ResourceKey[] = []) => {
  if (subscriptions.length === 0) return () => {};
  return (dispatch: Dispatch<any>) => {
    const quotes = subscriptions.map((key) => JSON.parse(key.toString()) as QuoteKey);
    if (getQuotesUseWs()) {
      dispatch(fetchQuotesByWs(quotes));
    } else {
      dispatch<any>(fetchQuoteBatch(quotes));
    }
  };
};

export const subscribeQuote = (location: LocationType, requests: QuoteKey[]) => (dispatch: Dispatch<any>) => {
  dispatch<any>(
    frontendActions.subscribe({
      onPoll: onPollQuote,
      location,
      apiName: MARKET_DATA_API_NAMES.QuoteBatchGet3,
      storeName: MARKET_DATA_STORE_NAME,
      resourceKeys: requests.map((request) => JSON.stringify(request)),
      pollTimeout: getConfig().pollTimeout
    })
  );
};

export const unsubscribeQuote = (location: LocationType, requests: QuoteKey[]) => (dispatch: Dispatch<any>) => {
  dispatch<any>(
    frontendActions.unsubscribe({
      location,
      apiName: MARKET_DATA_API_NAMES.QuoteBatchGet3,
      storeName: MARKET_DATA_STORE_NAME,
      resourceKeys: requests.map((request) => JSON.stringify(request))
    })
  );
};
